/**
 * 轮询逻辑工具
 */

export interface PollingOptions {
  interval: number
  maxAttempts?: number
  timeout?: number
  stopOnError?: boolean
  onPoll?: (attempt: number) => void
  onError?: (error: any) => void
}

const DEFAULT_POLLING_OPTIONS: PollingOptions = {
  interval: 1000,
  stopOnError: false
}

/**
 * 轮询类
 */
export class Polling {
  private options: PollingOptions
  private timerId: any = null
  private attempt = 0
  private startTime = 0
  private isRunning = false

  constructor(options: Partial<PollingOptions> = {}) {
    this.options = { ...DEFAULT_POLLING_OPTIONS, ...options }
  }

  /**
   * 开始轮询
   */
  async start<T>(fn: () => Promise<T>, condition: (result: T) => boolean): Promise<T> {
    if (this.isRunning) {
      throw new Error('Polling is already running')
    }

    this.isRunning = true
    this.attempt = 0
    this.startTime = Date.now()

    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          this.attempt++

          // 检查是否超过最大尝试次数
          if (this.options.maxAttempts && this.attempt > this.options.maxAttempts) {
            this.stop()
            reject(new Error('Max attempts reached'))
            return
          }

          // 检查是否超时
          if (this.options.timeout) {
            const elapsed = Date.now() - this.startTime
            if (elapsed > this.options.timeout) {
              this.stop()
              reject(new Error('Polling timeout'))
              return
            }
          }

          // 调用轮询回调
          if (this.options.onPoll) {
            this.options.onPoll(this.attempt)
          }

          // 执行函数
          const result = await fn()

          // 检查条件
          if (condition(result)) {
            this.stop()
            resolve(result)
            return
          }

          // 继续轮询
          if (this.isRunning) {
            this.timerId = setTimeout(poll, this.options.interval)
          }
        } catch (error) {
          // 调用错误回调
          if (this.options.onError) {
            this.options.onError(error)
          }

          // 根据配置决定是否停止
          if (this.options.stopOnError) {
            this.stop()
            reject(error)
          } else {
            // 继续轮询
            if (this.isRunning) {
              this.timerId = setTimeout(poll, this.options.interval)
            }
          }
        }
      }

      // 开始第一次轮询
      poll()
    })
  }

  /**
   * 停止轮询
   */
  stop(): void {
    if (this.timerId) {
      clearTimeout(this.timerId)
      this.timerId = null
    }
    this.isRunning = false
  }

  /**
   * 检查是否正在运行
   */
  isActive(): boolean {
    return this.isRunning
  }

  /**
   * 获取当前尝试次数
   */
  getAttempt(): number {
    return this.attempt
  }
}

/**
 * 创建轮询实例
 */
export function createPolling(options: Partial<PollingOptions> = {}): Polling {
  return new Polling(options)
}

/**
 * 简单轮询函数
 */
export async function poll<T>(
  fn: () => Promise<T>,
  condition: (result: T) => boolean,
  options: Partial<PollingOptions> = {}
): Promise<T> {
  const polling = new Polling(options)
  return polling.start(fn, condition)
}
